import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { Clock, Trophy, Calendar, Newspaper, Award, FileText, ExternalLink, MapPin, ArrowRight } from "lucide-react";
import { showError } from "@/lib/errors";

const TYPE_META = {
  competition: { label: "Compétition", Icon: Trophy, tone: "text-[hsl(var(--aito-lagoon))] border-[hsl(var(--aito-lagoon))]/40 bg-[hsl(var(--aito-lagoon))]/10" },
  event:       { label: "Calendrier",  Icon: Calendar, tone: "text-amber-700 border-amber-400/40 bg-amber-100/60 dark:text-amber-300" },
  achievement: { label: "Palmarès",    Icon: Award,   tone: "text-amber-700 border-amber-500/50 bg-[hsl(var(--aito-gold))]/20" },
  article:     { label: "Article",     Icon: Newspaper, tone: "text-slate-700 border-slate-300 bg-slate-100 dark:text-slate-200 dark:bg-slate-700/30" },
  memory:      { label: "Mémoire",     Icon: FileText, tone: "text-purple-700 border-purple-300 bg-purple-100" },
};

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function TimelineItem({ event, showAthleteName = false }) {
  const meta = TYPE_META[event.type] || TYPE_META.article;
  const Icon = meta.Icon;
  const isExt = event.link && /^https?:\/\//.test(event.link);
  const LinkComp = isExt ? "a" : Link;
  const linkProps = isExt ? { href: event.link, target: "_blank", rel: "noreferrer" } : { to: event.link };
  return (
    <div className="flex gap-3 items-start" data-testid={`entity-timeline-event-${event.id}`}>
      <div className={`shrink-0 h-8 w-8 rounded-full border ${meta.tone} flex items-center justify-center mt-0.5`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0 pb-3 border-b border-border last:border-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
          <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
          {event.discipline && <Badge variant="secondary" className="text-[10px] capitalize">{event.discipline}</Badge>}
          {event.medal && <Badge className="text-[10px] bg-[hsl(var(--aito-gold))] text-black">🏅 {event.medal}</Badge>}
          {event.date && <span className="text-[10px] text-muted-foreground ml-auto">{fmtDate(event.date)}</span>}
        </div>
        {event.link ? (
          <LinkComp {...linkProps} className="font-display font-semibold text-sm leading-tight hover:text-[hsl(var(--aito-lagoon))] line-clamp-2 block">
            {event.title}
            {isExt && <ExternalLink className="h-3 w-3 inline ml-1" />}
          </LinkComp>
        ) : (
          <p className="font-display font-semibold text-sm leading-tight">{event.title}</p>
        )}
        <div className="text-[11px] text-muted-foreground mt-0.5 space-x-2">
          {showAthleteName && event.athlete_name && (
            <Link to={`/athletes/${event.athlete_slug || event.athlete_id}`} className="text-[hsl(var(--aito-lagoon))] hover:underline">
              {event.athlete_name}
            </Link>
          )}
          {event.competition_name && event.type !== "competition" && <span>{event.competition_name}</span>}
          {event.result && <span>{event.result}</span>}
          {(event.extra?.location || event.extra?.place) && (
            <span className="inline-flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{event.extra?.location || event.extra?.place}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Timeline contextualisée à une entité (athlete/club/coach/federation/competition).
 *
 * Props :
 *  - entityType : "athlete" | "club" | "federation" | "competition"
 *  - entityId   : id de l'entité
 *  - limit      : nombre max d'événements affichés
 *  - showAthleteName : afficher l'athlète (utile sur fiche club/coach/fédé)
 *  - road2027   : ajoute le filtre road_to_2027=true (pour la timeline R2027)
 *  - extraParams: paramètres complémentaires
 */
export function EntityTimeline({ entityType, entityId, limit = 50, showAthleteName = false, road2027 = false, extraParams = {} }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    const params = { limit, ...extraParams };
    // V9 : si entityType est un des types reconnus, on filtre par {type}_id.
    // Sinon (ex: 'any' sur la page Road to 2027 globale), on ne pose pas de filtre id.
    const VALID = new Set(["athlete", "club", "coach", "federation", "competition"]);
    if (VALID.has(entityType)) {
      params[`${entityType}_id`] = entityId;
    }
    if (road2027) params.road_to_2027 = true;
    api.get("/timeline", { params })
      .then((r) => setEvents(r.data.items || []))
      .catch((e) => { setEvents([]); showError(e, { prefix: "Timeline :" }); })
      .finally(() => setLoading(false));
  }, [entityType, entityId, limit, road2027, JSON.stringify(extraParams)]); // eslint-disable-line

  if (loading) {
    return (
      <div className="space-y-2" data-testid="entity-timeline-loading">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }
  if (!events || events.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="Timeline vide pour cette entité."
        description="Les événements (compétitions, palmarès, articles, calendrier) apparaîtront ici dès qu'ils seront validés et rattachés."
      />
    );
  }
  return (
    <div className="space-y-3" data-testid="entity-timeline">
      {events.map((e) => <TimelineItem key={e.id} event={e} showAthleteName={showAthleteName} />)}
      <div className="text-right pt-2">
        <Button asChild variant="link" size="sm" data-testid="entity-timeline-see-all">
          <Link to="/timeline">Voir toute la timeline <ArrowRight className="h-3 w-3 ml-1" /></Link>
        </Button>
      </div>
    </div>
  );
}
